import * as React from 'react';
import type { MoveKind } from '@chess/api-model';
import { Skeleton } from '@chess/api-skeleton';
import { TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { clockRows, familyNote, familyRows, KIND_WORDS, materialRows, pawns, scannedNote, severityRows, standingRows, weekColumns, type GroupRow } from './cards.ts';
import { cardTitle, helpId, type CardId } from './help.tsx';
import { GroupBars, type BarRow } from './GroupBars.tsx';
import { WeekColumns } from './WeekColumns.tsx';
import type { MistakeGroups } from './MistakeGroupsAggregator.ts';
import { sampleGroups } from './sample.ts';

// One of the six cards of the Blunders or Misses screen, its body only (the
// section draws the frame: `cardMeta`, `registerCard`). Every bar, label and
// column opens its games in the library (`mistake=<key>`), the numbers
// counted the way the library's filter counts (`MistakeGroupsAggregator`).
// Drawn from props only; while loading, the same card over sample numbers
// gives the skeleton its shape.

export type MistakeCardProps = CardProps<
  { groups: MistakeGroups },
  {
    kind: MoveKind;
    card: CardId;
    /** Opens the library filtered to a bar's games. */
    link?: FilterLink;
  }
>;

/** The frame's title and help topic: the registration's and the stories'. */
export const cardMeta = (kind: MoveKind, card: CardId) => ({ title: cardTitle(kind, card), help: helpId(kind, card) });

/** The kind's colour: the review's grade colours. */
export const KIND_COLOUR: Record<MoveKind, string> = { blunder: 'var(--viz-grade-blunder)', miss: 'var(--viz-grade-miss)' };
const SEVERITY_COLOUR = { decisive: 'var(--viz-grade-blunder)', serious: 'var(--viz-grade-mistake)', minor: 'var(--viz-grade-inaccuracy)' } as const;

const gamesWord = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

function aside(kind: MoveKind, card: CardId): string {
  const verb = KIND_WORDS[kind].costVerb;
  switch (card) {
    case 'patterns':
      return `pawns ${verb} · games`;
    case 'standing':
      return `games · pawns ${verb}`;
    case 'weeks':
      return `${KIND_WORDS[kind].many} per game`;
    default:
      return 'games';
  }
}

/** A row drawn by its games. */
const byGames = (r: GroupRow, colour: string, title: string): BarRow => ({ key: r.key, label: r.label, value: r.games, valueText: String(r.games), games: r.games, colour, title });

function rowsOf(kind: MoveKind, card: Exclude<CardId, 'weeks'>, groups: MistakeGroups): BarRow[] {
  const colour = KIND_COLOUR[kind];
  const { one, many, costVerb } = KIND_WORDS[kind];
  const opens = (r: GroupRow) => (r.games ? ' · opens those games' : '');
  switch (card) {
    case 'patterns':
      return familyRows(groups).map((r) => ({
        key: r.key,
        label: r.label,
        value: r.cost,
        valueText: pawns(r.cost),
        extra: String(r.games),
        games: r.games,
        colour,
        title: `${r.label}: ${pawns(r.cost)} pawns ${costVerb} in ${r.mistakes} ${r.mistakes === 1 ? one : many}, in ${gamesWord(r.games)}${opens(r)}`,
      }));
    case 'severity':
      return severityRows(groups).map((r) => byGames(r, SEVERITY_COLOUR[r.severity], `${r.label}: ${gamesWord(r.games)} with a ${r.label.toLowerCase()} ${one}${opens(r)}`));
    case 'standing':
      return standingRows(groups).map((r) => ({
        ...byGames(r, colour, `${r.label} before the move: ${gamesWord(r.games)}, ${pawns(r.cost)} pawns ${costVerb}${opens(r)}`),
        extra: pawns(r.cost),
      }));
    case 'clock':
      return clockRows(groups).map((r) => byGames(r, colour, `${r.label} on your clock: ${gamesWord(r.games)}${opens(r)}`));
    case 'material':
      return materialRows(groups).map((r) => byGames(r, colour, `${r.label}: ${gamesWord(r.games)}${opens(r)}`));
  }
}

const Body: React.FC<{ kind: MoveKind; card: CardId; groups: MistakeGroups; link?: FilterLink }> = ({ kind, card, groups, link }) =>
  card === 'weeks' ? (
    <WeekColumns columns={weekColumns(groups)} colour={KIND_COLOUR[kind]} noun={KIND_WORDS[kind].many} link={link} />
  ) : (
    <GroupBars rows={rowsOf(kind, card, groups)} link={link} label={cardTitle(kind, card)} />
  );

function note(kind: MoveKind, card: CardId, groups: MistakeGroups): string {
  const base = scannedNote(groups);
  if (card === 'patterns') return familyNote(groups);
  if (card === 'clock' && groups.noClock) return `${base} · ${groups.noClock} without clock times`;
  if (card === 'weeks') return `${groups.mistakes} ${groups.mistakes === 1 ? KIND_WORDS[kind].one : KIND_WORDS[kind].many} ${base}`;
  return base;
}

export const MistakeCard: React.FC<MistakeCardProps> = (props) => {
  const { kind, card } = props;
  const { many } = KIND_WORDS[kind];
  if (props.loading) {
    const sample = sampleGroups(kind);
    return (
      <>
        <CardAside>{aside(kind, card)}</CardAside>
        <Skeleton loading shape={<Body kind={kind} card={card} groups={sample} />} label={`Loading ${cardTitle(kind, card).toLowerCase()}`} />
        <CardNote>
          <TextSkeleton sample={note(kind, card, sample)} />
        </CardNote>
      </>
    );
  }
  const { groups, link } = props;
  let body: React.ReactNode;
  if (!groups.scanned) body = <CardEmpty>Scan games to find your {many}.</CardEmpty>;
  else if (!groups.games) body = <CardEmpty>No {many} in the last 30 days.</CardEmpty>;
  else if (card === 'clock' && groups.noClock === groups.games) body = <CardEmpty>None of these games recorded clock times.</CardEmpty>;
  else body = <Body kind={kind} card={card} groups={groups} link={link} />;
  return (
    <>
      <CardAside>{aside(kind, card)}</CardAside>
      {body}
      {groups.games > 0 && <CardNote>{note(kind, card, groups)}</CardNote>}
    </>
  );
};
